import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

const STATUS_COLOR = {
  COMPLETED: "bg-success",
  CANCELLED: "bg-danger",
  PENDING: "bg-warning text-dark",
};

export default function AllTransfers() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  // useEffect(() => {
  //   const roles = JSON.parse(localStorage.getItem("roles") || "[]");
  //   if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
  //   if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
  // }, [navigate, token]);

  const fetchTransfers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-transfers?page=0&size=200`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setTransfers(Array.isArray(data) ? data : Array.isArray(data.content) ? data.content : []);
    } catch { setTransfers([]); toast.error("Failed to load transfers."); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (token) fetchTransfers(); }, [token]);

  const downloadReport = async () => {
    try {
      setDownloading(true);
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter !== "ALL") params.set("status", statusFilter);
      if (typeFilter) params.set("type", typeFilter);
      const res = await fetch(`${API_BASE_URL}/api/asset-transfers/report?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to download report");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      const hasFilters = search || statusFilter !== "ALL" || typeFilter;
      a.href = url;
      a.download = hasFilters ? `Transfers_Filtered_${date}.xlsx` : `Transfers_${date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded successfully");
    } catch (err) {
      toast.error(err.message || "Failed to download");
    } finally {
      setDownloading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return transfers.filter(t => {
      const matchStatus = statusFilter === "ALL" || (t.status || "PENDING") === statusFilter;
      const matchType = !typeFilter || (t.transferType || "") === typeFilter;
      if (!q) return matchStatus && matchType;
      return matchStatus && matchType && (
        (t.assetCode || "").toLowerCase().includes(q) ||
        (t.assetName || "").toLowerCase().includes(q) ||
        (t.fromEmployeeName || "").toLowerCase().includes(q) ||
        (t.toEmployeeName || "").toLowerCase().includes(q)
      );
    });
  }, [transfers, search, statusFilter, typeFilter]);

  const completed = transfers.filter(t => t.status === "COMPLETED").length;
  const pending = transfers.filter(t => !t.status || t.status === "PENDING").length;
  const cancelled = transfers.filter(t => t.status === "CANCELLED").length;

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="fw-bold mb-0"><i className="bi bi-arrow-left-right me-2"></i> All Transfers</h4>
            <div className="text-muted mt-1" style={{ fontSize: 13 }}>View and track all asset transfers across employees, departments, and locations.</div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-primary btn-sm" onClick={() => navigate("/newtransfer")}>
              <i className="bi bi-plus-circle me-1"></i> New Transfer
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate("/allassets")}>
              <i className="bi bi-box-seam me-1"></i> Asset List
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={downloadReport} disabled={downloading}>
              <i className="bi bi-download me-1"></i>{downloading ? "Downloading..." : "Export Excel"}
            </button>
          </div>
        </div>

        <div className="row g-2 mb-3">
          {[
            { label: "Total Transfers", value: transfers.length, icon: "bi-arrow-left-right", color: "#1e3a5f" },
            { label: "Completed", value: completed, icon: "bi-check-circle", color: "#198754" },
            { label: "Pending", value: pending, icon: "bi-hourglass-split", color: "#fd7e14" },
            { label: "Cancelled", value: cancelled, icon: "bi-x-circle", color: "#dc3545" },
          ].map(item => (
            <div className="col-md-3 col-6" key={item.label}>
              <div className="bg-white border rounded p-2 d-flex align-items-center gap-2">
                <i className={`bi ${item.icon}`} style={{ color: item.color, fontSize: 20 }}></i>
                <div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>{item.label}</div>
                  <div className="fw-bold" style={{ fontSize: 18, color: item.color }}>{item.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="p-2 bg-light border mb-2" style={{ cursor: "pointer" }} onClick={() => setShowFilters(p => !p)}>
          <strong>{showFilters ? "− Filters" : "+ Filters"}</strong>
        </div>
        {showFilters && (
          <div className="row mb-3 g-2">
            <div className="col-md-4">
              <input type="text" className="form-control form-control-sm"
                placeholder="Search by asset code, name, from/to employee..."
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="col-md-2">
              <select className="form-select form-select-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="ALL">All Statuses</option>
                <option value="COMPLETED">Completed</option>
                <option value="PENDING">Pending</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
            <div className="col-md-2">
              <select className="form-select form-select-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                <option value="">All Types</option>
                <option value="INTER_EMPLOYEE">Inter Employee</option>
                <option value="INTER_DEPT">Inter Dept</option>
                <option value="INTER_LOCATION">Inter Location</option>
                <option value="TEMPORARY">Temporary</option>
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSearch(""); setStatusFilter("ALL"); setTypeFilter(""); }}>Clear</button>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center p-2 bg-light border mb-2">
          <span className="text-muted" style={{ fontSize: 13 }}>{filtered.length} transfer{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
            <div className="text-muted mt-2" style={{ fontSize: 13 }}>Loading transfers...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i className="bi bi-arrow-left-right" style={{ fontSize: 32, opacity: 0.3 }}></i>
            <div className="mt-2">No transfer records found.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table table-sm align-middle text-start">
              <thead className="table-light">
                <tr>
                   <th>View | Edit</th>
                  <th>Asset</th><th>From</th><th>To</th><th>Type</th><th>Date</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.id}>
                    <td>
                          <Link to={`/viewtransfer/${item.id}`} className="me-1 text-primary" style={{ fontSize: 16 }}>View</Link>|
                          <Link to={`/edittransfer/${item.id}`} className="ms-1 text-primary" style={{ fontSize: 16 }}>Edit</Link>
                        </td>
                    <td>
                      <span className="badge" style={{ background: "#e8f0f8", color: "#1e3a5f", border: "1px solid #c8d8e8", fontFamily: "monospace" }}>
                        {item.assetCode || "—"}
                      </span>
                      <span className="ms-2" style={{ fontSize: 13 }}>{item.assetName}</span>
                    </td>
                    <td style={{ fontSize: 13 }}>{item.fromEmployeeName || "—"}</td>
                    <td style={{ fontSize: 13 }}>{item.toEmployeeName || "—"}</td>
                    <td><span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>{item.transferType || "—"}</span></td>
                    <td style={{ fontSize: 13 }}>{item.transferDate || "—"}</td>
                    <td>
                      <span className={`badge ${STATUS_COLOR[item.status] || "bg-warning text-dark"}`}>
                        {item.status || "PENDING"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
